<!-- Product states (PD §5): 3 skeleton cards (P/proto-states.js:130), the error panel, the gone-listing panel (:121). -->
@if (loading) {
    <agu-skeleton [Count]="3" />
} @else if (error) {
    <agu-state-panel
        Variant="error"
        [Icon]="ErrorCopy.Icon"
        [Title]="ErrorCopy.Title"
        [Body]="ErrorCopy.Body"
        PrimaryLabel="Try again"
        [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
        [SecondaryHref]="failures.SupportHref"
        [Foot]="failures.Foot"
        [Note]="failures.Note"
        (Primary)="Retry()"
    />
} @else if (!job) {
    <agu-state-panel [Icon]="GoneCopy.Icon" [Title]="GoneCopy.Title" [Body]="GoneCopy.Body" PrimaryLabel="See similar" SecondaryLabel="Back to Home" (Primary)="SeeSimilar.emit()" (Secondary)="BackToHome.emit()" />
} @else {
    <!-- The crumb trail (P/detail.html:888): "Back to {origin}" / this record. Hidden on a phone, where the app bar carries it (P/Member_Hub.html:493). -->
    <agu-crumb [Trail]="Crumb.Trail" (Navigate)="Crumb.Navigate($event)" />

    <!--
      R-F11: the mockup's plain document head — pills, then a 30px h1, then the
      14.5px meta line, all on the page ground. `detail.html` has no tinted
      band; the module accent reaches this view through the finder page host's
      `data-accent`.
    -->
    @if (IsRecommended || job.isNew || job.remote || CompanyTypeTag) {
        <div class="ra-dv__pills">
            @if (IsRecommended) {
                <span class="ra-dv__rectag">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="ra-dv__rectag-star">
                        <path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" />
                    </svg>
                    Recommended
                </span>
            }
            @if (job.isNew) {
                <span class="ra-dv__newpill">New</span>
            }
            @if (job.remote) {
                <span class="ra-dv__tagpill">Remote</span>
            }
            @if (CompanyTypeTag) {
                <span class="ra-dv__tagpill">{{ CompanyTypeTag }}</span>
            }
        </div>
    }

    <div class="ra-dv__head">
        @if (LogoUrl) {
            <img [src]="LogoUrl" alt="" loading="lazy" referrerpolicy="no-referrer" class="ra-dv__logo" (error)="OnLogoError()" />
        } @else {
            <span class="ra-dv__initials" aria-hidden="true">{{ CompanyInitials }}</span>
        }
        <div class="ra-dv__head-text">
            <h1 class="ra-dv__title">{{ job.title }}</h1>
            @if (MetaLine) {
                <p class="ra-dv__meta">{{ MetaLine }}</p>
            }
        </div>
    </div>

    <!--
      The mockup's three job tiles — Est. salary, Work location, Posted (detail.html:393, `.facts`
      repeat(3,1fr) at :272). Employment type and seniority are not tiles there (user ruling,
      2026-10-05); Posted stays the relative age.
    -->
    <div class="ra-dv__facts">
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">{{ SalaryLabel }}</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">{{ SalaryLine }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Work location</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">{{ WorkLocationLine }}</div>
        </div>
        <div class="ra-dv__fact">
            <div class="ra-dv__fact-k">Posted</div>
            <div class="ra-dv__fact-v ra-dv__fact-v--sm">{{ PostedLine }}</div>
        </div>
    </div>

    <section class="ra-dv__sec" aria-labelledby="ra-job-detail-about-h">
        <h2 class="ra-dv__body-h" id="ra-job-detail-about-h">About</h2>
        <div class="ra-dv__desc">
            @if (DescriptionParagraphs.length === 0) {
                <p>No description was provided by the employer.</p>
            }
            @for (paragraph of DescriptionParagraphs; track $index) {
                <p>{{ paragraph }}</p>
            }
        </div>
    </section>

    @if (job.companyDescription) {
        <section class="ra-dv__sec" aria-labelledby="ra-job-detail-aboutco-h">
            <h2 class="ra-dv__body-h" id="ra-job-detail-aboutco-h">{{ AboutHeading }}</h2>
            <p #aboutCo class="ra-dv__desc ra-dv__about-co" [class.ra-dv__about-co--expanded]="AboutExpanded">{{ job.companyDescription }}</p>
            @if (AboutClamped) {
                <button type="button" class="ra-dv__showmore" [attr.aria-expanded]="AboutExpanded" (click)="OnToggleAbout()">
                    {{ AboutExpanded ? 'Show less' : 'Show more' }}
                </button>
            }
        </section>
    }

    @if (BenefitsVisible.length > 0) {
        <section class="ra-dv__sec" aria-labelledby="ra-job-detail-benefits-h">
            <h2 class="ra-dv__body-h" id="ra-job-detail-benefits-h">Benefits</h2>
            <ul class="ra-dv__topics">
                @for (benefit of (ShowAllBenefits ? BenefitsAll : BenefitsVisible); track benefit) {
                    <li class="ra-dv__topic">{{ benefit }}</li>
                }
                @if (BenefitsMore > 0) {
                    <li>
                        <button type="button" class="ra-dv__topic ra-dv__topic--more" [attr.aria-expanded]="ShowAllBenefits" (click)="OnToggleBenefits()">
                            {{ ShowAllBenefits ? 'Show fewer' : '+' + BenefitsMore + ' more' }}
                        </button>
                    </li>
                }
            </ul>
        </section>
    }

    <!-- The primary action leads, ahead of Save (detail.html:506-510, `#job`); Pin is live-only and follows Save. -->
    <div class="ra-dv__acts">
        @if (PrimaryApply) {
            <a class="ra-dv__act ra-dv__act--primary" [href]="PrimaryApply.url" target="_blank" rel="noopener noreferrer"><span class="ra-dv__act-label">Apply on {{ PrimaryApply.label }}</span> <span class="ra-dv__act-arrow" aria-hidden="true">↗</span></a>
        } @else {
            <button type="button" class="ra-dv__act ra-dv__act--primary" disabled aria-disabled="true"><span class="ra-dv__act-label">Apply</span> <span class="ra-dv__act-arrow" aria-hidden="true">↗</span></button>
        }
        <button type="button" class="ra-dv__act ra-dv__act--ghost" [attr.aria-pressed]="job.itemState.saved" (click)="OnSaveToggle()">
            <agu-icon Name="saved" [Size]="15" />
            <span class="ra-dv__act-label">{{ job.itemState.saved ? 'Saved' : 'Save' }}</span>
        </button>
        <button type="button" class="ra-dv__act ra-dv__act--ghost" [attr.aria-pressed]="job.itemState.pinned" (click)="OnPinToggle()">
            <i class="fa-solid fa-thumbtack" aria-hidden="true"></i>
            <span class="ra-dv__act-label">{{ job.itemState.pinned ? 'Pinned' : 'Pin' }}</span>
        </button>
    </div>
    @if (OtherApply.length) {
        <p class="ra-dv__also">
            Also on
            @for (l of OtherApply; track l.site) {
                <a [href]="l.url" target="_blank" rel="noopener noreferrer">{{ l.label }}</a>
                @if (!$last) {
                    ·
                }
            }
        </p>
    }
}
